import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, EMPTY, exhaustMap, of } from 'rxjs';
import { catchError, concatMap, map, tap, withLatestFrom } from 'rxjs/operators';
import { stringifyIfNotNullOrUndefined, stringifyIfNotUndefined } from '../../../helper/helper';
import { CustomerEntityFromBackend, CustomerEntityGQLFields, CustomerStatusEnum } from '../../entities/customer.entity';
import { OfficeEntity, OfficeEntityFromBackend } from '../../entities/office.entity';
import { PersonEntity, PersonEntityFromBackend } from '../../entities/person.entity';
import { HttpService } from '../../services/http.service';
import { DaveActions } from '../actions/actions';
import { BaseActionTypes } from '../actions/base.actions';
import { CustomerActionTypes } from '../actions/customers.actions';
import { OfficeActionTypes } from '../actions/office.actions';
import { PersonActionTypes } from '../actions/person.actions';
import { ResolverLoadCustomerTypes } from '../actions/resolver.actions';
import { State } from '../index';
import { getToken } from '../selectors/base.selectors';
import { getCustomers } from '../selectors/customers.selectors';
import { getOffices } from '../selectors/offices.selectors';
import { getPersons } from '../selectors/person.selectors';
import { TaxTypeActions } from '../actions/tax-type.actions';
import { CustomerDataService } from '../../services/customer-data.service';

enum ErrorCodes {
    Add = 'Kunde Hinzufügen fehlgeschlagen',
    Load = 'Kunden Abrufen fehlgeschlagen',
    LoadImage = 'Kunden-Bild Abrufen fehlgeschlagen',
    SetUser = 'Nutzer Setzen fehlgeschlagen',
    Modify = 'Kunde Bearbeiten fehlgeschlagen',
    Remove = 'Kunde Löschen fehlgeschlagen',
}

@Injectable()
export class CustomerEffects {
    AddCustomer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerActionTypes.AddCustomer),
            map(
                ({ Payload }) => `
            mutation {
                createCustomer(
                    customerStatus: ${CustomerStatusEnum.Aktiv}
                    name: ${JSON.stringify(Payload.name)}
                    ${stringifyIfNotNullOrUndefined(Payload, 'customerNo')}
                    partnerId: ${JSON.stringify(Payload.partnerId)}
                    ${stringifyIfNotNullOrUndefined(Payload, 'description')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'customerSpecificationTypeId')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'hqLocation')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'gerichtsort')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'sales')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'employeeNumber')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'userRanking')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'customerTypeIds')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'homepage')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'mandateTypeIds')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'automaticCustomerNo')}
                      ${stringifyIfNotNullOrUndefined(Payload, 'automaticAccountsReceivableLedger')}
                      ${stringifyIfNotNullOrUndefined(Payload, 'ustId')}
                      ${stringifyIfNotNullOrUndefined(Payload, 'steuernummer')}
                      ${stringifyIfNotNullOrUndefined(Payload, 'partnerOfficeId')}
                      ${stringifyIfNotNullOrUndefined(Payload, 'debitorNo')}
                      ${stringifyIfNotUndefined(Payload, 'sachkontoCOAId')}
                      ${stringifyIfNotUndefined(Payload, 'kontoCOAId')}
                      ${stringifyIfNotUndefined(Payload, 'buyerReference')}
                      ${stringifyIfNotUndefined(Payload, 'paymentTerms')}
                      ${stringifyIfNotUndefined(Payload, 'paymentTargetInDays')}
                      ${stringifyIfNotUndefined(Payload, 'paymentDiscount')}
                      ${stringifyIfNotUndefined(Payload, 'paymentDiscountTargetInDays')}
                      ${stringifyIfNotUndefined(Payload, 'maintenanceFee')}
                      ${stringifyIfNotUndefined(Payload, 'addedMySupplierCustomerNumber')}
                      ${stringifyIfNotUndefined(Payload, 'taxTypeId')}
                ) {
                    ${CustomerEntityGQLFields}
                }
            }`,
            ),
            withLatestFrom(this.store$.select(getToken)),
            concatMap(([query, token]) => this.gatewayHttpService.graphQl({ query }, { token })),
            map((res) => res && res.createCustomer),
            withLatestFrom(this.store$.select(getCustomers)),
            concatMap(([res, customers]) =>
                res
                    ? [
                          CustomerActionTypes.UpdateAll({
                              Payload: [...customers, CustomerEntityFromBackend(res)],
                          }),
                      ]
                    : [BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: ErrorCodes.Add } })],
            ),
            catchError(
                (err, caught) => (
                    this.store$.dispatch(
                        BaseActionTypes.ErrorAction({
                            Payload: {
                                ToasterMessage: ErrorCodes.Add,
                                Err: err,
                                Caught: caught,
                            },
                        }),
                    ),
                    caught
                ),
            ),
        ),
    );

    AddCustomerAndOfficeAndPerson$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerActionTypes.AddCustomerAndOffice),
            map(
                ({ Payload }) => `
            mutation {
                createCustomerWithOfficePerson(
                    customerStatus: ${CustomerStatusEnum.Aktiv}
                    name: ${JSON.stringify(Payload.name)}
                    partnerId: ${JSON.stringify(Payload.partnerId)}
                    ${stringifyIfNotNullOrUndefined(Payload, 'customerNo')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'description')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'customerSpecificationTypeId')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'hqLocation')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'gerichtsort')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'sales')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'employeeNumber')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'userRanking')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'customerTypeIds')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'homepage')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'mandateTypeIds')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'automaticCustomerNo')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'automaticAccountsReceivableLedger')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'ustId')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'steuernummer')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'partnerOfficeId')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'debitorNo')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'sachkontoCOAId')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'kontoCOAId')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'buyerReference')}
                    ${stringifyIfNotUndefined(Payload, 'paymentTerms')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'paymentTargetInDays')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'paymentDiscount')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'paymentDiscountTargetInDays')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'isSupplier')}
                    ${stringifyIfNotUndefined(Payload, 'maintenanceFee')}
                    ${stringifyIfNotUndefined(Payload, 'addedMySupplierCustomerNumber')}
                    ${stringifyIfNotUndefined(Payload, 'taxTypeId')}


                    office: ${
                        Payload.office
                            ? `{
                        isHeadquarter: ${JSON.stringify(Payload.office.isHeadquarter)}
                        ${stringifyIfNotNullOrUndefined(Payload.office, 'email')}
                        ${stringifyIfNotNullOrUndefined(Payload.office, 'invoiceEmail')}
                        ${stringifyIfNotNullOrUndefined(Payload.office, 'phoneNumber')}
                        ${stringifyIfNotNullOrUndefined(Payload.office, 'street')}
                        ${stringifyIfNotNullOrUndefined(Payload.office, 'postalCode')}
                        ${stringifyIfNotNullOrUndefined(Payload.office, 'city')}
                        ${stringifyIfNotNullOrUndefined(Payload.office, 'country')}
                        ${stringifyIfNotNullOrUndefined(Payload.office, 'longitude')}
                        ${stringifyIfNotNullOrUndefined(Payload.office, 'latitude')}
                        ${stringifyIfNotNullOrUndefined(Payload.office, 'faxNumber')}
                        ${stringifyIfNotNullOrUndefined(Payload.office, 'mobileNumber')}
                        ${stringifyIfNotNullOrUndefined(Payload.office, 'iBAN')}
                        ${stringifyIfNotNullOrUndefined(Payload.office, 'swift')}
                        ${stringifyIfNotNullOrUndefined(Payload.office, 'bankName')}
                        ${stringifyIfNotNullOrUndefined(Payload.office, 'bankLocation')}
                        ${stringifyIfNotNullOrUndefined(Payload.office, 'bankPostalCode')}
                        ${stringifyIfNotNullOrUndefined(Payload.office, 'bankSortCode')}
                        ${stringifyIfNotNullOrUndefined(Payload.office, 'mobileNumber2')}
                    }`
                            : 'null'
                    }
                    person: ${
                        Payload.person
                            ? `{
                        ${stringifyIfNotNullOrUndefined(Payload.person, 'firstname')}
                        ${stringifyIfNotNullOrUndefined(Payload.person, 'lastname')}
                        ${stringifyIfNotNullOrUndefined(Payload.person, 'email')}
                        ${stringifyIfNotNullOrUndefined(Payload.person, 'phoneNumber')}
                        ${stringifyIfNotNullOrUndefined(Payload.person, 'mobileNumber')}
                        ${stringifyIfNotNullOrUndefined(Payload.person, 'faxNumber')}
                        ${stringifyIfNotNullOrUndefined(Payload.person, 'description')}
                    }`
                            : 'null'
                    }
                ) {
                    customer{
                        ${CustomerEntityGQLFields}
                    }
                    office {
                        ${OfficeEntity.GQLFields.join(',')}
                    }
                    person {
                        ${PersonEntity.GQLFields.join(',')}
                    }
                }
            }`,
            ),
            withLatestFrom(this.store$.select(getToken)),
            concatMap(([query, token]) => this.gatewayHttpService.graphQl({ query }, { token })),
            map((res) => res && res.createCustomerWithOfficePerson),
            withLatestFrom(
                combineLatest([this.store$.select(getOffices), this.store$.select(getPersons), this.store$.select(getCustomers)]).pipe(
                    map(([offices, persons, customers]) => ({
                        customers,
                        offices,
                        persons,
                    })),
                ),
            ),
            concatMap(([res, state]) => {
                return res
                    ? [
                          CustomerActionTypes.UpdateAll({
                              Payload: [...state.customers, CustomerEntityFromBackend(res.customer)],
                          }),
                          OfficeActionTypes.UpdateAll({
                              Payload: [...state.offices, OfficeEntityFromBackend(res.office)],
                          }),
                          res.person
                              ? PersonActionTypes.UpdateAll({
                                    Payload: [...state.persons, PersonEntityFromBackend(res.person)],
                                })
                              : null,
                          CustomerActionTypes.AddCustomerAndOfficeSuccess({
                              Payload: {
                                  Customer: res.customer && CustomerEntityFromBackend(res.customer),
                                  Office: res.office && OfficeEntityFromBackend(res.office),
                                  Person: res.person && PersonEntityFromBackend(res.person),
                              },
                          }),
                      ].filter((action) => action != null)
                    : [BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: ErrorCodes.Add } })];
            }),
            catchError((err, caught) =>
                of(
                    BaseActionTypes.ErrorAction({
                        Payload: {
                            ToasterMessage: ErrorCodes.Add,
                            Err: err,
                            Caught: caught,
                        },
                    }),
                ),
            ),
            tap(() => this.store$.dispatch(new ResolverLoadCustomerTypes())), // ToDo wofür ist das nötig?
        ),
    );


    LoadCustomers$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(CustomerActionTypes.Load),
                exhaustMap(({ Payload }) => {
                    console.log('CustomerActionTypes - Get', Payload);
                    return this.customerDataService.getAll$({ UpdatedSince: Payload.updatedSince }).pipe(
                        // withLatestFrom(this.store$.select(getTaxTypeDictionary)),
                        map((res) => {
                            // if (!isNotNullOrUndefined(Payload.updatedSince)) {
                            //     return TaxTypeActions.loadSuccess({ Payload: res });
                            // } else {
                            //     const fRes = res.filter((p) => {
                            //         const fromState = state[p.Id];
                            //         return !fromState || fromState.UpdatedAt.getTime() !== p.UpdatedAt.getTime();
                            //     });
                            console.log('CustomerActionTypes - Get success', res);

                            if (res) {
                                if (Payload?.updatedSince) {
                                    return CustomerActionTypes.UpdateMany({
                                        Payload: res,
                                        updateLatestUpdatedAt: true,
                                    });
                                } else {
                                    return CustomerActionTypes.UpdateAll({
                                        Payload: res,
                                        updateLatestUpdatedAt: true,
                                    });
                                }
                            } else {
                                return BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: ErrorCodes.Load } });
                            }
                            // }
                        }),
                        catchError(err => of(TaxTypeActions.loadFailure({ Errors: err, ToasterMessage: ErrorCodes.Load }))),
                    );
                }),
            ),
    );

    // LoadCustomers$ = createEffect(
    //     () =>
    //         this.actions$.pipe(
    //             ofType(CustomerActionTypes.Load),
    //             withLatestFrom(this.store$.select(getToken)),
    //             exhaustMap(([{ Payload }, token]) => {
    //                 return this.customerDataService.getAll$({ UpdatedSince: Payload.updatedSince }).pipe(
    //                     tap((res) => {
    //                         if (res) {
    //                             if (Payload?.updatedSince) {
    //                                 if (res.length) {
    //                                     this.store$.dispatch(
    //                                         CustomerActionTypes.UpdateMany({
    //                                             Payload: res,
    //                                             updateLatestUpdatedAt: true,
    //                                         }),
    //                                     );
    //                                 }
    //                             } else {
    //                                 this.store$.dispatch(
    //                                     CustomerActionTypes.UpdateAll({
    //                                         Payload: res,
    //                                         updateLatestUpdatedAt: true,
    //                                     }),
    //                                 );
    //                             }
    //                         } else {
    //                             this.store$.dispatch(BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: ErrorCodes.Load } }));
    //                         }
    //                     }),
    //                     catchError((err, caught) => {
    //                         console.log('### error catch')
    //                         this.store$.dispatch(
    //                             BaseActionTypes.ErrorAction({
    //                                 Payload: {
    //                                     ToasterMessage: ErrorCodes.Load,
    //                                     Err: err,
    //                                     Caught: caught,
    //                                 },
    //                             }),
    //                         );
    //                         return EMPTY;
    //                     }),
    //                 );
    //             }),
    //         ),
    //     { dispatch: false },
    // );

    ModifyCustomer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerActionTypes.ModifyCustomer),
            withLatestFrom(this.store$),
            concatMap(([{ Payload }, store]) => {
                const queryString = `
            mutation {
                changeCustomer(
                    id: ${JSON.stringify(Payload.id)}
                    gerichtsort: ${JSON.stringify(Payload.gerichtsort)}
                    ${stringifyIfNotNullOrUndefined(Payload, 'name')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'customerNo')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'description')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'customerSpecificationTypeId')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'hqLocation')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'sales')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'employeeNumber')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'userRanking')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'customerTypeIds')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'homepage')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'mandateTypeIds')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'automaticCustomerNo')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'automaticAccountsReceivableLedger')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'ustId')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'steuernummer')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'debitorNo')}
                    ${stringifyIfNotUndefined(Payload, 'sachkontoCOAId')}
                    ${stringifyIfNotUndefined(Payload, 'kontoCOAId')}
                    ${stringifyIfNotUndefined(Payload, 'buyerReference')}
                    ${stringifyIfNotUndefined(Payload, 'paymentTerms')}
                    ${stringifyIfNotUndefined(Payload, 'paymentTargetInDays')}
                    ${stringifyIfNotUndefined(Payload, 'paymentDiscount')}
                    ${stringifyIfNotUndefined(Payload, 'paymentDiscountTargetInDays')}
                    ${stringifyIfNotUndefined(Payload, 'maintenanceFee')}
                    ${stringifyIfNotUndefined(Payload, 'addedMySupplierCustomerNumber')}
                    ${stringifyIfNotUndefined(Payload, 'deletedMySupplierCustomerNumber')}
                    partnerOfficeId: ${JSON.stringify(Payload.partnerOfficeId)}
                    ${stringifyIfNotUndefined(Payload, 'taxTypeId')}
                    ${Payload.customerStatus ? `customerStatus: ${Payload.customerStatus}` : ''}
                      ) {
                        ${CustomerEntityGQLFields}
                }
            }`;
                return this.gatewayHttpService.graphQl({ query: queryString }, { token: store.base.token }).pipe(
                    map((res) =>
                        res && res.changeCustomer && res.changeCustomer.id
                            ? CustomerActionTypes.UpdateMany({
                                  Payload: [CustomerEntityFromBackend(res.changeCustomer)],
                              })
                            : BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: ErrorCodes.Modify } }),
                    ),
                    catchError((err, caught) =>
                        of(
                            BaseActionTypes.ErrorAction({
                                Payload: {
                                    ToasterMessage: ErrorCodes.Modify,
                                    Err: err,
                                    Caught: caught,
                                },
                            }),
                        ),
                    ),
                );
            }),
        ),
    );

    DeleteCustomer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerActionTypes.DeleteCustomer),
            withLatestFrom(this.store$),
            concatMap(([{ Payload }, store]) => {
                const queryString = `
            mutation {
                deleteCustomer(id: ${Payload})
            }`;

                return this.gatewayHttpService.graphQl({ query: queryString }, { token: store.base.token }).pipe(
                    map((res) => (res && res.deleteCustomer ? CustomerActionTypes.RemoveOne({ Payload }) : BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: ErrorCodes.Remove } }))),
                    catchError((err, caught) =>
                        of(
                            BaseActionTypes.ErrorAction({
                                Payload: {
                                    ToasterMessage: ErrorCodes.Remove,
                                    Err: err,
                                    Caught: caught,
                                },
                            }),
                        ),
                    ),
                );
            }),
        ),
    );

    constructor(private actions$: Actions<DaveActions>, private store$: Store<State>, private gatewayHttpService: HttpService, private customerDataService: CustomerDataService) {}
}
